/** SUNTRONIX ONE — ONE AI Workspace (Phase 1).
 *  Premium AI tool catalogue + a permission/tenant-scoped ASK ONE chat.
 *  Propose-only: the assistant never writes, never sends, WhatsApp disabled. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, ShieldCheck, Lock, Search, BarChart3,
  PenLine, BadgeCheck, ArrowLeft, Cpu, Boxes, Users, FileText,
} from "lucide-react";

const MODELS = [
  { id: "gemini", provider: "gemini", label: "Gemini 3 Flash", note: "Fast · default" },
  { id: "gpt-5.6", provider: "openai", model: "gpt-5.6-luna", label: "ChatGPT · GPT-5.6", note: "OpenAI · balanced" },
  { id: "gpt-5.6-sol", provider: "openai", model: "gpt-5.6-sol", label: "ChatGPT · GPT-5.6 Sol", note: "OpenAI · deep reasoning" },
  { id: "gpt-5.5", provider: "openai", model: "gpt-5.5", label: "ChatGPT · GPT-5.5", note: "OpenAI" },
  { id: "gpt-5.4-mini", provider: "openai", model: "gpt-5.4-mini", label: "ChatGPT · GPT-5.4 mini", note: "OpenAI · fast & light" },
  { id: "claude", provider: "anthropic", label: "Claude Sonnet 5", note: "Careful & precise" },
];

const GROUPS = [
  { key: "ASK", label: "Ask", icon: Search },
  { key: "ANALYSE", label: "Analyse", icon: BarChart3 },
  { key: "CREATE", label: "Create", icon: PenLine },
  { key: "VERIFY", label: "Verify", icon: BadgeCheck },
];

const TOOL_ICON = {
  ASK_ONE: Sparkles, STOCK_AI: Boxes, CUSTOMER_AI: Users, REPORT_AI: FileText,
};

const STARTERS = {
  ASK_ONE: [
    "How much inventory do we currently hold?",
    "What should I look at first today?",
    "Summarise what data you can see for me.",
  ],
  STOCK_AI: [
    "How many serialised units are active right now?",
    "How many stock lots are on hand?",
    "Is there anything unusual about current stock?",
  ],
  CUSTOMER_AI: [
    "Summarise our authorised customer relationships.",
    "What is the next best action for a key account?",
  ],
  REPORT_AI: [
    "Give me a short operations summary for today.",
    "What needs my attention right now?",
  ],
};

export default function OneAiWorkspace() {
  const [params, setParams] = useSearchParams();
  const [catalogue, setCatalogue] = useState({ tools: [], enabled: true });
  const [loadingCat, setLoadingCat] = useState(true);
  const activeKey = params.get("tool");

  useEffect(() => {
    let alive = true;
    api.get("/one-ai/tools")
      .then(({ data }) => { if (alive) setCatalogue(data); })
      .catch(() => { if (alive) toast.error("Could not load ONE AI tools"); })
      .finally(() => { if (alive) setLoadingCat(false); });
    return () => { alive = false; };
  }, []);

  const activeTool = useMemo(
    () => catalogue.tools.find((t) => t.key === activeKey && t.status === "LIVE"),
    [catalogue.tools, activeKey],
  );

  if (activeTool) {
    return (
      <ToolChat
        tool={activeTool}
        disabled={!catalogue.enabled}
        onBack={() => { params.delete("tool"); setParams(params, { replace: true }); }}
      />
    );
  }

  return (
    <div className="space-y-8" data-testid="one-ai-workspace">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-6 md:p-8">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20 blur-3xl"
             style={{ background: "var(--st-accent)" }} aria-hidden />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--st-border)] px-3 py-1 text-[11px] font-medium tracking-wide text-[color:var(--st-text-muted)]">
            <Sparkles className="h-3.5 w-3.5" style={{ color: "var(--st-accent)" }} strokeWidth={1.8} />
            ONE AI · Enterprise Intelligence
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-[color:var(--st-text)]">
            Your context-aware AI workspace
          </h1>
          <p className="mt-2 max-w-2xl text-sm md:text-base text-[color:var(--st-text-muted)]">
            Ask questions and generate drafts across the data you are authorised to see.
            ONE AI is tenant-isolated, role-restricted and <strong>propose-only</strong> — it
            never performs financial, stock or document actions without your approval.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <SafetyChip icon={ShieldCheck} label="Tenant-isolated" />
            <SafetyChip icon={Lock} label="Role-restricted" />
            <SafetyChip icon={BadgeCheck} label="Propose-only · you approve writes" />
          </div>
        </div>
      </div>

      {!catalogue.enabled && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
             data-testid="one-ai-disabled-banner">
          ONE AI is currently disabled by your administrator.
        </div>
      )}

      {/* Tool groups */}
      {loadingCat ? (
        <div className="flex items-center gap-2 text-sm text-[color:var(--st-text-subtle)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tools…
        </div>
      ) : (
        GROUPS.map((g) => {
          const tools = catalogue.tools.filter((t) => t.group === g.key);
          if (!tools.length) return null;
          const GIcon = g.icon;
          return (
            <section key={g.key} data-testid={`one-ai-group-${g.key}`}>
              <div className="mb-3 flex items-center gap-2">
                <GIcon className="h-4 w-4 text-[color:var(--st-text-muted)]" strokeWidth={1.8} />
                <h2 className="text-sm font-semibold uppercase tracking-wide text-[color:var(--st-text-muted)]">
                  {g.label}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tools.map((t) => (
                  <ToolCard
                    key={t.key}
                    tool={t}
                    disabled={!catalogue.enabled}
                    onOpen={() => { params.set("tool", t.key); setParams(params); }}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}

function SafetyChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--st-surface-alt)] px-3 py-1 text-[11px] font-medium text-[color:var(--st-text-muted)]">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} /> {label}
    </span>
  );
}

function ToolCard({ tool, onOpen, disabled }) {
  const live = tool.status === "LIVE";
  const Icon = TOOL_ICON[tool.key] || Cpu;
  const clickable = live && !disabled;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={clickable ? onOpen : undefined}
      data-testid={`one-ai-tool-${tool.key}`}
      className={
        "group text-left rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-5 transition-all " +
        (clickable
          ? "hover:border-[color:var(--st-accent)] hover:shadow-md cursor-pointer"
          : "opacity-70 cursor-default")
      }
    >
      <div className="flex items-start justify-between">
        <div className="grid h-10 w-10 place-items-center rounded-lg"
             style={{ background: "color-mix(in srgb, var(--st-accent) 12%, transparent)", color: "var(--st-accent)" }}>
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </div>
        {live
          ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">LIVE</span>
          : <span className="rounded-full bg-[color:var(--st-surface-alt)] px-2 py-0.5 text-[10px] font-semibold text-[color:var(--st-text-subtle)]">SOON</span>}
      </div>
      <div className="mt-3 text-[15px] font-semibold text-[color:var(--st-text)]">{tool.name}</div>
      <div className="mt-1 text-[13px] leading-relaxed text-[color:var(--st-text-muted)]">{tool.desc}</div>
    </button>
  );
}

function ToolChat({ tool, onBack, disabled }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [modelId, setModelId] = useState("gemini");
  const selectedModel = MODELS.find((m) => m.id === modelId) || MODELS[0];
  const [sessionId] = useState(() => `oneai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const bodyRef = useRef(null);
  const Icon = TOOL_ICON[tool.key] || Cpu;
  const starters = STARTERS[tool.key] || STARTERS.ASK_ONE;

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, loading]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading || disabled) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/one-ai/ask", {
        question, tool: tool.key, session_id: sessionId,
        provider: selectedModel.provider, model: selectedModel.model,
        scope: tool.name,
      });
      setMsgs((m) => [...m, {
        role: "one", text: data.answer, confidence: data.confidence,
        source: data.source, model: data.model,
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "ONE AI unavailable");
      setMsgs((m) => [...m, { role: "one", text: "Sorry — I couldn't answer that just now. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-11rem)] min-h-[520px]" data-testid="one-ai-chat">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-[color:var(--st-border)]">
        <button onClick={onBack} data-testid="one-ai-chat-back"
          className="h-9 w-9 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-muted)]">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
        </button>
        <div className="grid h-10 w-10 place-items-center rounded-lg"
             style={{ background: "color-mix(in srgb, var(--st-accent) 12%, transparent)", color: "var(--st-accent)" }}>
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-[color:var(--st-text)] truncate">{tool.name}</div>
          <div className="text-[11px] text-[color:var(--st-text-subtle)] truncate">{tool.desc}</div>
        </div>
        <div className="ml-auto">
          <label className="sr-only" htmlFor="one-ai-model">Model</label>
          <select
            id="one-ai-model"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            data-testid="one-ai-model-select"
            className="rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-3 py-1.5 text-[12px] text-[color:var(--st-text)] focus:outline-none focus:ring-1 focus:ring-[color:var(--st-accent)]"
          >
            {MODELS.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Body */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto py-4 space-y-4" data-testid="one-ai-chat-body">
        {msgs.length === 0 && (
          <div className="max-w-xl space-y-2">
            <div className="text-[13px] text-[color:var(--st-text-muted)]">Try asking:</div>
            {starters.map((s) => (
              <button key={s} onClick={() => ask(s)} data-testid="one-ai-starter"
                className="w-full text-left text-[13px] px-3 py-2.5 rounded-lg border border-[color:var(--st-border)] hover:border-[color:var(--st-accent)] hover:bg-[color:var(--st-surface-alt)] transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={"max-w-[80%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed " +
              (m.role === "user"
                ? "bg-[color:var(--st-text)] text-white"
                : "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text)]")}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              {m.role === "one" && (m.source || m.model) && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] opacity-60">
                  {m.confidence && <span className="uppercase tracking-wider">{m.confidence} confidence</span>}
                  {m.model && <span className="font-mono">{m.model}</span>}
                  {m.source && <span>· {m.source}</span>}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-[12px] text-[color:var(--st-text-subtle)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} /> ONE is thinking…
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="pt-3 border-t border-[color:var(--st-border)]" data-testid="one-ai-chat-form">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={disabled}
            placeholder={disabled ? "ONE AI is disabled" : `Ask ${tool.name}…`}
            data-testid="one-ai-chat-input"
            className="flex-1 px-4 py-2.5 text-[14px] rounded-lg border border-[color:var(--st-border)] bg-[color:var(--st-surface)] focus:outline-none focus:ring-1 focus:ring-[color:var(--st-accent)] disabled:opacity-50"
          />
          <button type="submit" disabled={loading || disabled || !input.trim()} data-testid="one-ai-chat-send"
            className="h-11 px-4 grid place-items-center rounded-lg text-white disabled:opacity-40"
            style={{ background: "var(--st-accent)" }}>
            <Send className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>
        <div className="mt-2 text-[11px] text-[color:var(--st-text-subtle)]">
          Answers use only your authorised, tenant-filtered data. ONE AI proposes — it never performs actions on its own.
        </div>
      </form>
    </div>
  );
}
